/**
 * Benvel WhatsApp FAB — Story 2.4 / FR4
 *
 * Vanilla web component that handles fallback routing and analytics emission
 * for the persistent WhatsApp floating action button. Enhancement-only: the
 * Liquid baseline ships a fully working <a href="https://wa.me/..."> without JS.
 *
 * PII guarantee: the analytics event payload contains ONLY { surface }.
 * No phone, msisdn, text, customer_id, or cart_token fields ever.
 * The benvel:whatsapp:click event is dispatched; consent gating is the
 * consumer's responsibility (Story 1.6 / NFR18 pattern). See
 * docs/runbooks/whatsapp-fab.md for the full analytics contract.
 */
class BenvelWhatsappFab extends HTMLElement {
  connectedCallback() {
    const link = this.querySelector(".benvel-whatsapp-fab__link");
    if (!link) return;

    this.applyFallbackIfNeeded(link);

    // PII-free surface identifier for analytics (template name only).
    const surface = this.dataset.surface || "unknown";

    link.addEventListener("click", () => {
      // Consent-gated analytics — Story 1.6 emitter pattern. NO PII.
      // Consumers MUST gate on Shopify.customerPrivacy.userCanBeTracked()
      // before transmitting. This component only dispatches; it does not send.
      try {
        const evt = new CustomEvent("benvel:whatsapp:click", {
          bubbles: true,
          detail: { surface: surface },
        });
        this.dispatchEvent(evt);
      } catch (_e) {
        // swallow — analytics is fire-and-forget
      }
    });
  }

  applyFallbackIfNeeded(link) {
    const fallbackUrl = this.dataset.fallbackUrl || "";
    if (!fallbackUrl || !this.shouldUseFallback(link.href)) return;

    link.href = fallbackUrl;
    link.removeAttribute("target");
    link.removeAttribute("rel");

    const fallbackLabel = this.dataset.fallbackLabel || "";
    if (fallbackLabel) {
      link.setAttribute("aria-label", fallbackLabel);
    }
  }

  shouldUseFallback(href) {
    try {
      const url = new URL(href);
      if (url.hostname !== "wa.me") return false;
    } catch (_e) {
      return true;
    }

    const userAgent = navigator.userAgent.toLowerCase();
    const webviewSignals = [
      "instagram",
      "fban",
      "fbav",
      "fb_iab",
      "micromessenger",
      "line/",
      "; wv",
      " wv)",
    ];

    return webviewSignals.some((signal) => userAgent.includes(signal));
  }
}

if (!customElements.get("benvel-whatsapp-fab")) {
  customElements.define("benvel-whatsapp-fab", BenvelWhatsappFab);
}
